import React, { useState } from "react";
import { Alert } from "@mui/material";
import { FileUploader } from "react-drag-drop-files";
import ButtonField from "../../Ui/ButtonField";
import { useTailwindBrand } from "../../shared";
import TravelRepository from "../../../Repositories/TravelRepository";
import { DeclarationStatusEnum } from "../../../Enum/UrssafStatusEnum";
import { ENV_DECLARONS, getActivePrestationBrandConfig, isBrandActive } from "../../../config/environment.config";

const FILE_TYPES = ["PDF", "PNG", "JPEG", "JPG"];

interface Props {
    courseId?: number | null;
    declarationStatus?: string | null;
    /** Statut renvoyé par le serveur après dépôt (en attente d'approbation). */
    onUploaded?: (declarationStatus: string) => void;
}

/**
 * Autre justificatif de déplacement pour un cours déjà enregistré : quand
 * l'abonnement ne couvre pas ce cours, ou que la preuve a été rejetée, le
 * prof dépose un titre de transport, un reçu ou une facture depuis la fiche
 * du cours. Visible seulement pour un cours en attente d'approbation ou
 * rejeté : rien n'est proposé quand aucune preuve n'est requise.
 */
const CourseTravelProofUpload = ({ courseId, declarationStatus, onUploaded }: Props) => {
    const { classes, brandConfig } = useTailwindBrand();
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [done, setDone] = useState(false);

    const relevant = declarationStatus === DeclarationStatusEnum.PENDING_APPROVAL
        || declarationStatus === DeclarationStatusEnum.REJECTED;
    if (!courseId || !relevant) return null;

    const fem = isBrandActive(ENV_DECLARONS) ? "e" : "";

    const handleUpload = async () => {
        if (!file) {
            setError("Choisissez d'abord un fichier.");
            return;
        }
        setError("");
        setLoading(true);
        try {
            const result = await TravelRepository.uploadTravelProof(courseId, file);
            setDone(true);
            setFile(null);
            onUploaded?.(result.declarationStatus);
        } catch (e) {
            console.warn("Dépôt de la preuve de déplacement impossible", e);
            setError("Le dépôt a échoué, réessayez plus tard.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 mb-3 space-y-2">
            <p className={`text-sm font-semibold ${classes.primaryText}`}>
                🧾 {declarationStatus === DeclarationStatusEnum.REJECTED ? "Nouvelle preuve de déplacement" : "Autre preuve de déplacement"}
            </p>
            <p className="text-xs text-gray-600">
                {declarationStatus === DeclarationStatusEnum.REJECTED
                    ? `Votre preuve a été rejetée : déposez-en une nouvelle (titre de transport, reçu, facture) pour que ${getActivePrestationBrandConfig("", "ce ", "cette ")}${getActivePrestationBrandConfig()} soit réexaminé${fem}.`
                    : `Si votre abonnement ne couvre pas ${getActivePrestationBrandConfig("", "ce ", "cette ")}${getActivePrestationBrandConfig()}, joignez un autre justificatif (titre de transport, reçu, facture). Il remplace le document déjà transmis.`}
            </p>
            {done ? (
                <Alert severity="success" variant="outlined" sx={{ fontSize: "0.8rem", fontFamily: brandConfig.fontFamily }}>
                    Preuve transmise : notre équipe l'examine avant de déclarer {getActivePrestationBrandConfig("", "le ", "la ")}{getActivePrestationBrandConfig()}.
                </Alert>
            ) : (
                <>
                    <FileUploader
                        handleChange={(f: File) => { setFile(f); setError(""); }}
                        name="preuve-cours"
                        types={FILE_TYPES}
                        label="Déposez ou cliquez pour ajouter votre justificatif"
                    />
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-xs">{file ? <>Fichier : <span className="font-semibold">{file.name}</span></> : null}</span>
                        <ButtonField size="small" variant="contained" label="Transmettre" loading={loading} disabled={loading || !file} onClick={handleUpload} />
                    </div>
                    {error && <p className="text-xs text-red-600">{error}</p>}
                </>
            )}
        </div>
    );
};

export default CourseTravelProofUpload;
